import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import Image from '@cartwhl/ui/atoms/Image';
import Loading from '@cartwhl/ui/atoms/Loading';
import Video from '@cartwhl/ui/atoms/Video';
import Text from '@cartwhl/ui/base/Text';
import LikeIcon from '@cartwhl/ui/icons/LikeIcon';

const box = tv({
  base: 'bg-gray-15 dark:bg-gray-75 relative flex w-full cursor-default items-center justify-center overflow-hidden rounded-lg transition-all',
  variants: {
    active: {
      true: 'shadow-outline-blue-3',
      false: '',
    },
    committed: {
      true: 'shadow-outline-faint',
    },
    interactive: {
      true: 'cursor-pointer',
      false: '',
    },
  },
  compoundVariants: [
    {
      interactive: true,
      active: false,
      className: 'hover:shadow-menu',
    },
    {
      interactive: true,
      active: true,
      className: 'cursor-default',
    },
    {
      committed: true,
      active: true,
      className: 'shadow-outline-blue-3',
    },
  ],
  defaultVariants: {
    active: false,
    interactive: true,
  },
});

const media = tv({
  base: 'relative z-[1] h-full w-full rounded-lg text-transparent',
  variants: {
    aspect: {
      square: 'aspect-square',
      animation: 'aspect-160/136',
      video: 'aspect-212/140',
    },
    imageFit: {
      cover: 'object-cover',
      contain: 'max-h-full w-auto max-w-full object-contain',
    },
  },
  defaultVariants: {
    aspect: 'square',
    imageFit: 'cover',
  },
});

export default function AnimationPreview({
  onClick,
  title,
  subtitle,
  mediaPath,
  mediaType,
  aspect,
  active,
  committed,
  loading,
  imageLoading,
  imageDecoding,
  imageFetchPriority,
  imageCrossOrigin = 'use-credentials',
  mediaBackdropColor,
  imageFit,
  className = '',
  isFavorited,
  optionSlot,
}: {
  onClick?: () => void | Promise<void>;
  title?: string;
  subtitle?: string;
  mediaPath: string;
  mediaType: 'video' | 'image';
  aspect?: VariantProps<typeof media>['aspect'];
  active?: boolean;
  committed?: boolean;
  loading?: boolean;
  imageLoading?: 'eager' | 'lazy';
  imageDecoding?: 'sync' | 'async' | 'auto';
  imageFetchPriority?: 'high' | 'low' | 'auto';
  imageCrossOrigin?: 'anonymous' | 'use-credentials';
  mediaBackdropColor?: string;
  imageFit?: VariantProps<typeof media>['imageFit'];
  className?: string;
  isFavorited?: boolean;
  optionSlot?: ReactNode;
}) {
  const [failedMediaSourceKey, setFailedMediaSourceKey] = useState<
    string | null
  >(null);
  const [fadeInVideo, setFadeInVideo] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const mediaSourceKey = `${mediaType}:${mediaPath}`;
  const showMedia =
    Boolean(mediaPath) && failedMediaSourceKey !== mediaSourceKey;

  useEffect(() => {
    setFailedMediaSourceKey(null);
    setFadeInVideo(false);
  }, [mediaSourceKey]);

  const handleImageMediaError = (event: SyntheticEvent<HTMLImageElement>) => {
    const source = event.currentTarget.getAttribute('src');
    if (source !== mediaPath) {
      return;
    }
    setFailedMediaSourceKey(mediaSourceKey);
  };

  const handleVideoMediaError = (details?: {
    code?: number;
    currentSrc: string;
  }) => {
    if (details?.currentSrc !== mediaPath) {
      return;
    }
    setFailedMediaSourceKey(mediaSourceKey);
  };

  const handleClick = async () => {
    if (!onClick) return;

    const result = onClick();
    if (result instanceof Promise) {
      setIsPending(true);
      try {
        await result;
      } finally {
        setIsPending(false);
      }
    }
  };

  const isLoading = loading || isPending;

  return (
    <div className={`flex w-full flex-col ${className}`}>
      <button
        onClick={handleClick}
        className={box({ active, committed, interactive: !!onClick })}
      >
        <div
          className={`bg-loading-backdrop absolute inset-0 flex items-center justify-center rounded-[inherit] ${isLoading ? 'opacity-100' : 'opacity-0'}`}
        >
          <Loading className="text-green-35" trackClassName="stroke-gray-30" />
        </div>
        {mediaBackdropColor && showMedia && mediaType === 'image' ? (
          <div
            className="absolute inset-0 rounded-[inherit]"
            style={{ backgroundColor: mediaBackdropColor }}
            aria-hidden
          />
        ) : null}
        {showMedia ? (
          <>
            {mediaType === 'video' ? (
              <Video
                key={mediaSourceKey}
                src={mediaPath}
                className={media({
                  aspect,
                  imageFit: 'cover',
                  className: `opacity-0 transition-opacity duration-200 ease-linear ${
                    fadeInVideo ? 'opacity-100' : ''
                  }`,
                })}
                mode="autoplay"
                onLoadedData={() => setFadeInVideo(true)}
                onError={handleVideoMediaError}
                crossOrigin={imageCrossOrigin}
              />
            ) : imageFit === 'contain' ? (
              <div
                className={media({
                  aspect,
                  className: 'flex items-center justify-center overflow-hidden',
                })}
              >
                <Image
                  key={mediaSourceKey}
                  alt={title}
                  src={mediaPath}
                  className="relative z-[1] max-h-full max-w-full object-contain"
                  onError={handleImageMediaError}
                  draggable="false"
                  crossOrigin={imageCrossOrigin}
                  loading={imageLoading}
                  decoding={imageDecoding}
                  fetchPriority={imageFetchPriority}
                />
              </div>
            ) : (
              <Image
                key={mediaSourceKey}
                alt={title}
                src={mediaPath}
                className={media({ aspect, imageFit: imageFit ?? 'cover' })}
                onError={handleImageMediaError}
                draggable="false"
                crossOrigin={imageCrossOrigin}
                loading={imageLoading}
                decoding={imageDecoding}
                fetchPriority={imageFetchPriority}
              />
            )}
          </>
        ) : (
          <div
            className={media({
              aspect,
              imageFit: 'cover',
              className: 'flex items-center justify-center',
            })}
          >
            <svg
              width="44"
              height="56"
              viewBox="0 0 44 56"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.9775 11.2379C20.9482 11.2379 20.0103 10.9889 19.1636 10.4908C18.3335 9.9928 17.6611 9.32043 17.1465 8.47375C16.6484 7.62708 16.3994 6.68909 16.3994 5.65979C16.3994 4.63049 16.6484 3.6925 17.1465 2.84583C17.6611 1.99915 18.3335 1.32678 19.1636 0.828735C20.0103 0.330689 20.9482 0.081665 21.9775 0.081665C23.0068 0.081665 23.9448 0.330689 24.7915 0.828735C25.6382 1.32678 26.3105 1.99915 26.8086 2.84583C27.3232 3.6925 27.5806 4.63049 27.5806 5.65979C27.5806 6.68909 27.3232 7.62708 26.8086 8.47375C26.3105 9.32043 25.6382 9.9928 24.7915 10.4908C23.9448 10.9889 23.0068 11.2379 21.9775 11.2379ZM21.9775 34.9449C21.4961 34.9449 21.1226 35.1276 20.8569 35.4928C20.6079 35.858 20.3423 36.4972 20.0601 37.4103C19.8774 37.9249 19.645 38.7135 19.3628 39.776C19.0806 40.8219 18.7651 42.0089 18.4165 43.337C18.0845 44.6486 17.7441 45.9767 17.3955 47.3214C17.0635 48.6661 16.7563 49.8946 16.4741 51.007C16.1919 52.1193 15.9595 52.9908 15.7769 53.6217C15.3618 55.1324 14.5317 55.8878 13.2866 55.8878C12.4565 55.8878 11.8174 55.5641 11.3691 54.9166C10.9209 54.2858 10.8213 53.5553 11.0703 52.7252C11.1035 52.609 11.2114 52.1359 11.394 51.3058C11.5933 50.4923 11.8423 49.4547 12.1411 48.193C12.4399 46.9479 12.7637 45.5948 13.1123 44.1339C13.4609 42.673 13.7847 41.2452 14.0835 39.8507C14.3989 38.4396 14.6728 37.1862 14.9053 36.0905C15.1543 34.9781 15.3203 34.1647 15.4033 33.65C15.5361 32.8365 15.6357 31.8488 15.7021 30.6866C15.7852 29.5079 15.8433 28.296 15.8765 27.0509C15.9097 25.8058 15.9263 24.6603 15.9263 23.6144C15.9429 22.5685 15.9512 21.7716 15.9512 21.2238C15.9346 20.4933 15.752 19.8956 15.4033 19.4308C15.0713 18.9659 14.6147 18.6754 14.0337 18.5592C13.7847 18.5094 13.2534 18.4347 12.4399 18.3351C11.6431 18.2189 10.7051 18.0944 9.62597 17.9615C8.56347 17.8121 7.49267 17.6627 6.41357 17.5133C5.33447 17.3639 4.37988 17.2311 3.5498 17.1149C2.71973 16.9821 2.16357 16.8824 1.88135 16.816C1.31689 16.6998 0.860351 16.4176 0.511718 15.9694C0.179687 15.5211 0.0136719 14.9899 0.0136719 14.3756C0.0136719 13.6285 0.237792 13.0392 0.686034 12.6075C1.15088 12.1593 1.67383 11.9352 2.25488 11.9352C2.4375 11.9352 2.62842 11.9601 2.82764 12.0099C3.04346 12.0431 3.26758 12.0846 3.5 12.1344C4.49609 12.3336 5.72461 12.5328 7.18555 12.7321C8.66308 12.9147 10.2568 13.089 11.9668 13.255C13.6934 13.4044 15.4199 13.5289 17.1465 13.6285C18.873 13.7115 20.4834 13.7531 21.9775 13.7531C23.4883 13.7531 25.1069 13.7032 26.8335 13.6036C28.5767 13.504 30.2949 13.3795 31.9883 13.2301C33.6982 13.0641 35.292 12.8898 36.7695 12.7072C38.2471 12.5079 39.4756 12.317 40.4551 12.1344C40.6875 12.0846 40.9033 12.0431 41.1025 12.0099C41.3184 11.9601 41.5342 11.9352 41.75 11.9352C42.3145 11.9352 42.8208 12.1593 43.269 12.6075C43.7339 13.0558 43.9663 13.6451 43.9663 14.3756C43.9663 14.9733 43.792 15.4879 43.4434 15.9196C43.1113 16.3512 42.6548 16.65 42.0737 16.816C41.8247 16.899 41.2852 17.007 40.4551 17.1398C39.6416 17.2726 38.687 17.4137 37.5913 17.5631C36.5122 17.6959 35.4248 17.8287 34.3291 17.9615C33.25 18.0944 32.3037 18.2189 31.4902 18.3351C30.6934 18.4347 30.1787 18.5094 29.9463 18.5592C29.3486 18.692 28.8838 18.9908 28.5518 19.4557C28.2363 19.9039 28.0786 20.4933 28.0786 21.2238C28.0786 21.7716 28.0786 22.5685 28.0786 23.6144C28.0952 24.6603 28.1118 25.8058 28.1284 27.0509C28.1616 28.296 28.2114 29.5079 28.2778 30.6866C28.3442 31.8488 28.4438 32.8365 28.5767 33.65C28.6431 34.1647 28.7925 34.9781 29.0249 36.0905C29.2573 37.1862 29.5312 38.4396 29.8467 39.8507C30.1787 41.2452 30.5107 42.673 30.8428 44.1339C31.1748 45.5948 31.4902 46.9479 31.7891 48.193C32.1045 49.4547 32.3618 50.4923 32.561 51.3058C32.7603 52.1359 32.8765 52.609 32.9097 52.7252C33.1255 53.5553 33.0176 54.2858 32.5859 54.9166C32.1543 55.5641 31.5234 55.8878 30.6934 55.8878C29.3652 55.8878 28.5352 55.1324 28.2031 53.6217C28.0537 52.9908 27.8379 52.111 27.5557 50.9821C27.29 49.8697 26.9829 48.6412 26.6343 47.2965C26.2856 45.9518 25.937 44.6154 25.5884 43.2872C25.2563 41.9591 24.9409 40.7721 24.6421 39.7262C24.3599 38.6803 24.1357 37.9083 23.9697 37.4103C23.7041 36.4972 23.4302 35.858 23.1479 35.4928C22.8657 35.1276 22.4756 34.9449 21.9775 34.9449Z"
                fill="#A7A7A7"
              />
            </svg>
          </div>
        )}
        {isFavorited && (
          <LikeIcon
            className="text-red pointer-events-none absolute left-2 top-2"
            fill={true}
          />
        )}
      </button>
      {title && (
        <div className="mt-2 flex items-center justify-between gap-1">
          <div className="flex min-w-0 flex-col gap-1">
            <Text variant="studio/body/sm" className="truncate">
              {title}
            </Text>
            {subtitle && (
              <Text variant="studio/body/sm" className="text-gray-45 truncate">
                {subtitle}
              </Text>
            )}
          </div>
          {optionSlot && <div>{optionSlot}</div>}
        </div>
      )}
    </div>
  );
}
